import { CenterSpinner } from '@/components/ats';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { KText } from '@/ui-library/components';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useGenerateOfferDocumentMutation, offerDetailsAPI } from '../../api';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { useOfferStore } from '../../store';
interface DocumentPreviewProps {
  offerId: string;
  templateId: string | null;
  hasValidationIssues: boolean;
  onRegenerateTrigger: () => void;
  regenerateKey: number;
  isTemplateSelectionComplete?: boolean;
  isMultiDoc?: boolean;
  selectedDocIndex?: number;
  // Wires the viewer's built-in "Replace" control to the offer's replace flow.
  onReplace?: (_files: File[]) => void;
  fileName?: string;
  showReplace?: boolean;
  onGenerationError?: (_docIndex: number) => void;
  toolbarLeftControl?: React.ReactNode;
  // Fires true once the PDF preview (with its own toolbar) is on screen.
  onPreviewReady?: (_isReady: boolean) => void;
  // Drop a removed document's already-generated PDF from the preview locally
  // (no regenerate). pdfRemovalNonce bumps per removal so each applies once.
  pdfRemovalIndex?: number;
  pdfRemovalNonce?: number;
}

interface OfferDocumentPdf {
  url: string;
  content_type: string;
}

interface OfferData {
  offer_status: string;
  offer_document_pdf?: OfferDocumentPdf;
  // Per-document PDFs (multi-doc), index-aligned with the template's offer_documents tabs.
  offer_documents_pdf?: OfferDocumentPdf[];
}

// Manual poll matching pollOfferWithRetries from offerFormHelpers. maxRetries*timeout is
// the FE fail window; allow ~90s so slow multi-doc generations don't time out (CORE-4795).
const pollOfferWithRetries = async (
  offerId: string,
  signal: AbortSignal,
  maxRetries = 30,
  timeout = 3000
): Promise<OfferData> => {
  let retries = 0;
  while (retries < maxRetries) {
    if (signal.aborted) throw new Error('Aborted');
    try {
      const response = await offerDetailsAPI.pollOfferById(offerId);
      if (response) {
        const status = response.offer_status;
        if (status === 'document_generated' || status === 'approved_internal') {
          return response;
        } else if (status === 'document_generation_error') {
          throw new Error('Document generation error');
        } else if (
          status !== 'pending_document_generation' &&
          status !== 'document_generation_in_progress'
        ) {
          throw new Error('Unexpected status');
        }
      }
    } catch (err: any) {
      if (
        err.message === 'Document generation error' ||
        err.message === 'Unexpected status' ||
        err.message === 'Aborted' ||
        retries === maxRetries - 1
      ) {
        throw err;
      }
    }
    retries += 1;
    await new Promise<void>((res, rej) => {
      const t = setTimeout(res, timeout);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          rej(new Error('Aborted'));
        },
        { once: true }
      );
    });
  }
  throw new Error('Maximum retries reached');
};

export const DocumentPreview = ({
  offerId,
  templateId,
  hasValidationIssues,
  onRegenerateTrigger,
  regenerateKey,
  isTemplateSelectionComplete = true,
  isMultiDoc: _isMultiDoc = false,
  selectedDocIndex = 0,
  onReplace,
  fileName,
  showReplace = false,
  onGenerationError,
  toolbarLeftControl,
  onPreviewReady,
  pdfRemovalIndex,
  pdfRemovalNonce,
}: DocumentPreviewProps) => {
  const { tOfferTemplateMultiDocuments: isCompact } = useKFlags();
  const isPolling = useOfferStore((state) => state.isPolling);
  const setIsPolling = useOfferStore((state) => state.setIsPolling);
  const setCurrentOffer = useOfferStore((state) => state.setCurrentOffer);
  const setEnableNextButton = useOfferStore(
    (state) => state.setEnableNextButton
  );
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // Identity of the in-flight generation; blocks duplicate runs for the same
  // template while a generation is still pending (multi-doc gate oscillation).
  const inFlightRef = useRef<string | null>(null);
  // Tracks the last successfully-completed run key so tab switches that flip
  // hasValidationIssues don't restart a generation that already finished.
  const completedKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  // Aborts the in-flight generateAndPoll when a new run supersedes it (template change).
  const abortRef = useRef<AbortController | null>(null);
  const generateDocument = useGenerateOfferDocumentMutation();
  const toast = useShowToast();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const offerStatus = offerData?.offer_status;
  // Prefer per-doc PDFs when the offer was generated as multi-doc (offer_documents_pdf
  // is present), regardless of whether the FE knows it's multi-doc yet — the template
  // list API may omit offer_documents so isMultiDoc can be false even for multi-doc offers.
  const selectedPdf = offerData?.offer_documents_pdf?.length
    ? offerData.offer_documents_pdf[selectedDocIndex]
    : offerData?.offer_document_pdf;
  const pdfUrl = selectedPdf?.url || '';
  const contentType = selectedPdf?.content_type || '';

  // Combined effect: Generate document and poll if needed
  useEffect(() => {
    if (
      !offerId ||
      !templateId ||
      hasValidationIssues ||
      !isTemplateSelectionComplete
    ) {
      return;
    }

    const runKey = `${offerId}:${templateId}:${regenerateKey}`;
    // Skip if a generation for this identity is already in-flight or completed (the multi-doc
    // gate flips across renders). Treat inFlightRef as live only while its abort is still active,
    // so a superseded-then-aborted key doesn't block a needed regeneration ("doc not loading").
    if (inFlightRef.current === runKey && !abortRef.current?.signal.aborted)
      return;
    if (completedKeyRef.current === runKey) return;

    // Abort the previous run so its result can't overwrite state for the new template.
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    inFlightRef.current = runKey;

    // Lift the new doc into the store for the email/send step — only the doc fields,
    // so a poll_status attachments echo can't clobber offerAttachments (CORE-4800/4796).
    const syncGeneratedDocToStore = (data: OfferData) => {
      const { offer_status, offer_document_pdf, offer_documents_pdf } = data;
      setCurrentOffer({
        ...useOfferStore.getState().currentOffer,
        offer_status,
        ...(offer_document_pdf !== undefined && { offer_document_pdf }),
        ...(offer_documents_pdf !== undefined && { offer_documents_pdf }),
      });
    };

    const generateAndPoll = async () => {
      setIsPolling(true);
      try {
        // Step 1: Generate document
        const result = await generateDocument.mutateAsync({
          id: offerId,
          event: 'generate_document',
        });

        if (!isMountedRef.current || abort.signal.aborted) return;

        const status = result?.offer_status;

        // Step 2: Poll if BE is still generating (either pending or in-progress state).
        if (
          status === 'pending_document_generation' ||
          status === 'document_generation_in_progress'
        ) {
          const polledData = await pollOfferWithRetries(offerId, abort.signal);
          if (isMountedRef.current && !abort.signal.aborted) {
            setOfferData(polledData);
            syncGeneratedDocToStore(polledData);
            completedKeyRef.current = runKey;
          }
        } else {
          if (isMountedRef.current && !abort.signal.aborted) {
            setOfferData(result);
            syncGeneratedDocToStore(result);
            completedKeyRef.current = runKey;
          }
        }
      } catch (err: any) {
        if (!isMountedRef.current || abort.signal.aborted) return;

        toast({
          title: 'Error generating offer',
          description: err.message || 'Failed to generate offer document',
          status: 'error',
        });
        setOfferData({
          offer_status: 'document_generation_error',
        });
        onGenerationError?.(selectedDocIndex);
      } finally {
        // If we still own the ref, no newer run superseded us — clear isPolling regardless
        // of whether the component is still mounted (unmount-while-generating must also clear).
        const wasOwner = inFlightRef.current === runKey;
        if (wasOwner) {
          inFlightRef.current = null;
          setIsPolling(false);
        }
      }
    };

    generateAndPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offerId,
    templateId,
    regenerateKey,
    hasValidationIssues,
    isTemplateSelectionComplete,
  ]);

  // Removing a doc doesn't change the others' PDFs, so drop the removed entry from the
  // generated list instead of regenerating (CORE-4681). Nonce-keyed so each removal applies once.
  useEffect(() => {
    if (!pdfRemovalNonce) return;
    setOfferData((prev) => {
      const list = prev?.offer_documents_pdf;
      if (
        !list?.length ||
        pdfRemovalIndex === undefined ||
        pdfRemovalIndex === null
      )
        return prev;
      return {
        ...prev,
        offer_documents_pdf: list.filter((_, i) => i !== pdfRemovalIndex),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfRemovalNonce]);

  const isGenerating =
    generateDocument.isPending ||
    isPolling ||
    offerStatus === 'pending_document_generation' ||
    offerStatus === 'document_generation_in_progress';

  const showPreview =
    pdfUrl &&
    !isGenerating &&
    !hasValidationIssues &&
    offerStatus !== 'document_generation_error';

  const showError =
    !isGenerating && offerStatus === 'document_generation_error';

  // Layout effect so the parent toggles its fallback toolbar before paint (no flash of both).
  useLayoutEffect(() => {
    onPreviewReady?.(!!showPreview);
  }, [showPreview, onPreviewReady]);

  useEffect(() => {
    if (isGenerating || showError) {
      setEnableNextButton(false);
    } else {
      setEnableNextButton(true);
    }
  }, [isGenerating, setEnableNextButton, showError]);

  // Measure container height before paint so the PDF fits on first render; a post-paint
  // effect starts at 0 and causes the "zoom in then normalize" flash (CORE-4791).
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        setContainerHeight(height);
      }
    };

    updateHeight();

    // Update height on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [showPreview]);

  // Don't render anything if there are validation issues
  if (hasValidationIssues) {
    return null;
  }

  return (
    // Multi-doc (compact) panel manages its own layout and should fill the drawer
    // height; the 120px carve-out is a legacy single-doc reservation.
    <Box h={isCompact ? '100%' : 'calc(100% - 120px)'} w="100%">
      {/* Loading State */}
      {isGenerating && (
        <Flex
          mt="12px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border={isCompact ? undefined : '1px solid'}
          borderColor={isCompact ? undefined : 'border-color'}
          borderRadius="sm"
        >
          <CenterSpinner h="100%" />
          <KText mt="12px" color="secondary-600">
            Generating offer document...
          </KText>
        </Flex>
      )}

      {/* Error State */}
      {showError && (
        <Flex
          mt="24px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border={isCompact ? undefined : '1px solid'}
          borderColor={isCompact ? undefined : 'border-color'}
          borderRadius="sm"
        >
          <Text mb={4}>Document generation failed</Text>
          <Button variant="primary" onClick={onRegenerateTrigger}>
            Re-generate Offer
          </Button>
        </Flex>
      )}

      {/* PDF Preview */}
      {showPreview && (
        <Flex direction="column" gap="12px" h="100%">
          <Box
            ref={containerRef}
            overflow="hidden"
            h="100%"
            minH="300px"
            className="document-preview"
          >
            <PDFReaderBrowser
              file={pdfUrl}
              content_type={contentType}
              preview_url={null}
              showReplaceControl={!!showReplace}
              handleUpload={onReplace}
              acceptedFiles={['.docx']}
              maxFiles={1}
              fileName={fileName}
              toolbarLeftControl={toolbarLeftControl}
              isCompact={isCompact}
              containerStyles={{
                h:
                  containerHeight > 0
                    ? `${containerHeight}px`
                    : 'calc(100% - 400px)',
              }}
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};
